import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { bringWindowToFrontAtom, floatingWindowResetAtom, floatingWindowStackAtom } from '@/atoms/floatingWindow.atom';
import {
  anchorToRect,
  clampToBounds,
  defaultWindowSizes,
  GRIP_HEIGHT_PX,
  offsetFromAnchor,
  readWindowOffset,
  rectFromOffset,
  windowZIndex,
  writeWindowOffset,
  WINDOW_DEFAULT_ANCHORS,
  WINDOW_MIN_VISIBLE_PX,
  type GripBox,
  type Rect,
  type Size,
  type ViewerWindowId,
  type WindowAnchor,
} from '@/lib/floatingWindow';
import { cn } from '@/lib/utils';

/** Arrow-key nudge, and its Shift multiple (spec §12.1). */
const NUDGE_PX = 8;
const NUDGE_SHIFT_PX = 32;

/**
 * The props the drag handle must spread. The consumer is expected to render a
 * real `<button>` (ruling D43) — hence `type: 'button'` rather than a
 * `role='button'` on a div, which announces as a button but behaves like
 * nothing.
 *
 * The `ref` is read by exactly one thing: the clamp, which has to know where
 * the grip sits inside the window (see `GripBox`). Grips are not centred —
 * the analysis column's is at its top-left, the minimap's at its top-right —
 * so "keep 24 px of the WINDOW on screen" kept the wrong 24 px and left the
 * grip off the edge, which is a window nobody can drag back.
 */
export interface FloatingWindowHandleProps {
  ref: (el: HTMLElement | null) => void;
  onPointerDown: (e: React.PointerEvent) => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  tabIndex: 0;
  type: 'button';
  'aria-label': string;
  className: string;
  /** The 44 px touch target, shared with the clamp that keeps it reachable. */
  style: { minHeight: number };
}

/**
 * Where the grip sits inside the window, in the window's own coordinates.
 *
 * Falls back to the whole window width and a `GRIP_HEIGHT_PX` row at its
 * top-left whenever there is nothing real to measure — no handle element yet,
 * or a layout that reports zeros (jsdom does, for everything). That fallback is
 * exactly the rule the clamp used before it knew about grips, so an unmeasured
 * window behaves as it always did rather than jumping somewhere new.
 */
function measureGrip(root: HTMLElement, grip: HTMLElement | null, size: Size): GripBox {
  const fallback: GripBox = { dx: 0, dy: 0, w: size.w, h: GRIP_HEIGHT_PX };
  if (!grip) return fallback;
  const g = grip.getBoundingClientRect();
  if (!g.width || !g.height) return fallback;
  const r = root.getBoundingClientRect();
  return { dx: g.left - r.left, dy: g.top - r.top, w: g.width, h: g.height };
}

export interface FloatingWindowProps {
  id: ViewerWindowId;
  /** The canvas region the window is confined to (spec §12.1). */
  boundsRef: React.RefObject<HTMLElement | null>;
  anchor?: WindowAnchor;
  /** Rendered as the drag handle. Receives the props it must spread. */
  handle: (handleProps: FloatingWindowHandleProps) => ReactNode;
  /** Accessible name for the handle and the window. */
  label: string;
  className?: string;
  /**
   * Keep the window mounted but out of sight and out of the tab order — for
   * adopters whose content must keep its effects alive while it has nothing
   * to show (the walk toolbar's clip-box restore). Folded into the same
   * inline `visibility`/`pointer-events` the primitive already drives, so it
   * cannot lose to them in the cascade the way a class would.
   */
  hidden?: boolean;
  /**
   * Hold the window at its anchor, ignoring the stored offset — for content
   * that takes over the whole region while it is open (the minimap's Expand,
   * spec §12.2 / ruling D57). A card sized to the region but positioned at a
   * dragged offset runs off the canvas, which is what the Expand control used
   * to do once the window had been moved.
   *
   * The offset is ignored, never rewritten: the window returns to it as soon
   * as the prop drops. That is also why a pinned window does not drag or
   * nudge — a committed move would overwrite the very offset being held.
   */
  atAnchor?: boolean;
  children?: ReactNode;
}

/**
 * A viewer overlay the user can move (spec §12.1).
 *
 * Position is kept as an OFFSET from the window's default anchor and persisted
 * per id; it is re-clamped whenever the bounds resize, so a window parked in a
 * corner of a wide viewport is still reachable on a narrow one. Escape during
 * a drag puts it back where the drag started — a cancelled drag that leaves the
 * window somewhere new is a lost position.
 *
 * The window never animates: it follows the pointer, and a nudged or re-clamped
 * window arrives immediately (spec §12.1 — "no snap animation"). The only
 * transition is the handle's hover tint, behind `motion-safe:`.
 */
export function FloatingWindow({
  id,
  boundsRef,
  anchor,
  handle,
  label,
  className,
  hidden = false,
  atAnchor = false,
  children,
}: FloatingWindowProps) {
  const resetTick = useAtomValue(floatingWindowResetAtom);
  const stack = useAtomValue(floatingWindowStackAtom);
  const bringToFront = useSetAtom(bringWindowToFrontAtom);
  const ref = useRef<HTMLDivElement | null>(null);
  const gripRef = useRef<HTMLElement | null>(null);
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  /** Written only inside pointer handlers — never read during render. */
  const dragRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    originX: number;
    originY: number;
  } | null>(null);

  // Held by value, not by identity: a caller passing `anchor={{ top: 8 }}`
  // inline would otherwise hand a fresh object to every effect below on every
  // render.
  const fallback = WINDOW_DEFAULT_ANCHORS[id];
  const top = anchor ? anchor.top : fallback.top;
  const bottom = anchor ? anchor.bottom : fallback.bottom;
  const left = anchor ? anchor.left : fallback.left;
  const right = anchor ? anchor.right : fallback.right;
  const resolvedAnchor = useMemo<WindowAnchor>(() => ({ top, bottom, left, right }), [top, bottom, left, right]);

  /** Stable, so spreading the handle props does not re-attach the ref every
   *  render. Nothing re-measures here: the ref lands before the layout effect
   *  that places the window, and the ResizeObserver covers every later change. */
  const setGrip = useCallback((el: HTMLElement | null) => {
    gripRef.current = el;
  }, []);

  /** Keep the previous object when nothing moved, so the effects below settle. */
  const place = useCallback((next: { x: number; y: number }) => {
    setPos((prev) => (prev && prev.x === next.x && prev.y === next.y ? prev : next));
  }, []);

  const measure = useCallback((): { bounds: Size; size: Size; grip: GripBox } | null => {
    const b = boundsRef.current;
    const el = ref.current;
    if (!b || !el) return null;
    const bounds = { w: b.clientWidth, h: b.clientHeight };
    const slot = defaultWindowSizes(bounds)[id];
    // The rendered size, not the declared slot: clamping has to keep the
    // REAL box on screen.
    const size = { w: el.offsetWidth || slot.w, h: el.offsetHeight || slot.h };
    return { bounds, size, grip: measureGrip(el, gripRef.current, size) };
  }, [boundsRef, id]);

  /** Anchor + stored offset, clamped. Re-run on mount, on reset and on resize.
   *  While `atAnchor` the offset is skipped — read the prop's doc for why it is
   *  skipped rather than cleared. */
  const reposition = useCallback(() => {
    const m = measure();
    if (!m) return;
    const stored = atAnchor ? null : readWindowOffset(id);
    const rect: Rect = stored
      ? rectFromOffset(stored, resolvedAnchor, m.size, m.bounds)
      : anchorToRect(resolvedAnchor, m.size, m.bounds);
    place(clampToBounds(rect, m.bounds, WINDOW_MIN_VISIBLE_PX, m.grip));
  }, [atAnchor, id, measure, place, resolvedAnchor]);

  // Layout effect: place before paint so the window never flashes at its
  // anchor before jumping to the stored position.
  useLayoutEffect(reposition, [reposition, resetTick]);

  useEffect(() => {
    // React attaches a parent's host ref AFTER its children's layout effects
    // run, so on the very first commit the layout effect above saw a null
    // `boundsRef` and left the window `visibility: hidden`. This passive effect
    // runs after the whole commit, when the ref is set — measuring here is what
    // guarantees the window is placed on the first paint rather than depending
    // on the ResizeObserver's initial callback (which never comes in jsdom).
    reposition();
    const b = boundsRef.current;
    if (!b) {
      // The bounds element mounts later still (a conditionally rendered
      // region): try again next frame rather than staying hidden forever.
      const raf = requestAnimationFrame(() => reposition());
      return () => cancelAnimationFrame(raf);
    }
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => reposition());
    ro.observe(b);
    // The window's own box matters too: one parked mostly off an edge that
    // then SHRINKS (a panel closes, the toolbar un-wraps) violates the clamp
    // and can disappear entirely until something re-runs it.
    const el = ref.current;
    if (el) ro.observe(el);
    return () => ro.disconnect();
  }, [boundsRef, reposition]);

  const commit = useCallback(
    (next: { x: number; y: number }) => {
      const m = measure();
      if (!m) return;
      const clamped = clampToBounds({ ...next, w: m.size.w, h: m.size.h }, m.bounds, WINDOW_MIN_VISIBLE_PX, m.grip);
      place(clamped);
      writeWindowOffset(
        id,
        offsetFromAnchor({ ...clamped, w: m.size.w, h: m.size.h }, resolvedAnchor, m.size, m.bounds),
      );
    },
    [id, measure, place, resolvedAnchor],
  );

  const onPointerDown = (e: React.PointerEvent) => {
    if (e.button !== 0 || atAnchor) return;
    // Bring-to-front is handled on the root in the capture phase, so a click
    // anywhere in the window raises it — not only one that finds the grip.
    // No `preventDefault()`: the handle is a focusable button, and swallowing the
    // default would also swallow the focus the keyboard nudge needs. Text
    // selection is handled by `select-none` on the handle instead.
    e.stopPropagation();
    if (!pos) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = { pointerId: e.pointerId, startX: e.clientX, startY: e.clientY, originX: pos.x, originY: pos.y };
  };

  useEffect(() => {
    const move = (e: PointerEvent) => {
      const d = dragRef.current;
      if (!d || d.pointerId !== e.pointerId) return;
      const m = measure();
      if (!m) return;
      place(
        clampToBounds(
          { x: d.originX + (e.clientX - d.startX), y: d.originY + (e.clientY - d.startY), w: m.size.w, h: m.size.h },
          m.bounds,
          WINDOW_MIN_VISIBLE_PX,
          m.grip,
        ),
      );
    };
    const up = (e: PointerEvent) => {
      const d = dragRef.current;
      if (!d || d.pointerId !== e.pointerId) return;
      dragRef.current = null;
      commit({ x: d.originX + (e.clientX - d.startX), y: d.originY + (e.clientY - d.startY) });
    };
    const key = (e: KeyboardEvent) => {
      const d = dragRef.current;
      if (!d || e.key !== 'Escape') return;
      dragRef.current = null;
      place({ x: d.originX, y: d.originY });
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
    window.addEventListener('keydown', key);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
      window.removeEventListener('keydown', key);
    };
  }, [commit, measure, place]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    const step = e.shiftKey ? NUDGE_SHIFT_PX : NUDGE_PX;
    const dx = e.key === 'ArrowLeft' ? -step : e.key === 'ArrowRight' ? step : 0;
    const dy = e.key === 'ArrowUp' ? -step : e.key === 'ArrowDown' ? step : 0;
    if ((!dx && !dy) || !pos || atAnchor) return;
    e.preventDefault();
    e.stopPropagation();
    bringToFront(id);
    commit({ x: pos.x + dx, y: pos.y + dy });
  };

  return (
    <div
      ref={ref}
      data-testid={`floating-window-${id}`}
      aria-hidden={hidden || undefined}
      // `aria-roledescription` and `aria-label` are ignored on the implicit
      // `generic` role a bare div carries, so the movable-window affordance
      // needs a real role to be announced at all (ruling D43).
      role='group'
      aria-roledescription='movable window'
      aria-label={label}
      onPointerDownCapture={() => bringToFront(id)}
      style={{
        left: pos?.x ?? 0,
        top: pos?.y ?? 0,
        zIndex: windowZIndex(stack, id),
        visibility: pos && !hidden ? 'visible' : 'hidden',
        pointerEvents: hidden ? 'none' : undefined,
      }}
      className={cn('pointer-events-auto absolute', className)}>
      {handle({
        ref: setGrip,
        onPointerDown,
        onKeyDown,
        tabIndex: 0,
        type: 'button',
        'aria-label': label,
        style: { minHeight: GRIP_HEIGHT_PX },
        // 44 px touch target (`GRIP_HEIGHT_PX`, spec §12.1) and the same quiet neutral/hover
        // tokens the viewer's `FloatingToolbar` buttons use, so a grip reads as
        // part of the same chrome. The focus ring matches `ui/button.tsx` — a
        // handle you cannot see you have focused is a handle you cannot use.
        className: cn(
          'flex min-w-11 cursor-grab touch-none items-center justify-center rounded-md outline-none select-none active:cursor-grabbing',
          'text-slate-500 hover:bg-slate-100 dark:text-stone-400 dark:hover:bg-stone-800',
          'focus-visible:ring-[3px] focus-visible:ring-ring/50 motion-safe:transition-colors',
        ),
      })}
      {children}
    </div>
  );
}
